import { AppInsightsCore, IExtendedConfiguration, IExtendedTelemetryItem, ITelemetryItem } from "@microsoft/1ds-core-js";
import { PostChannel } from "@microsoft/1ds-post-js";
import { IPropertyConfiguration, PropertiesPlugin } from "@microsoft/1ds-properties-js";
import { PrivacyGuardPlugin } from "@microsoft/1ds-privacy-guard-js";
import React from "react";
import { v4 } from "uuid";
import { EventContext } from "../../common/contexts/event";
import { useInterval } from "../../common/hooks/usetimeout";
import { getCookie } from "../../common/utilities/browser";
import { getFailureType } from "../../common/utilities/error";
import { ITelemetryEvent } from "../../common/utilities/platformdispatch";
import { ISessionContext, SessionContext } from "../contexts/session";
import { IFeatureContext } from "../../common/contexts/feature";
import { useSubscription } from "../../common/hooks/useobservable";

export const customerPromiseTable = "ev_customerpromise";
export const engagementTable = "ev_engagement";
export const experimentTable = "ev_experiment";
export const qosTable = "ev_qos";
export const unhandledErrorTable = "ev_unhandlederror";
export const privacyGuardEvent = "ev_privacyguard";

const telemetryTypePrefixMapping: { [key: string]: string } = {
  customerPromise: "CustomerPromise",
  engagement: "Engagement",
  exception: "Qos",
  fetchComplete: "Qos",
  fetchStart: "Qos",
  operationComplete: "Engagement",
  scenarioComplete: "Engagement",
  sessionComplete: "Engagement",
  userAction: "Engagement"
};

interface IFailedScenarioProperties {
  name: string;
  code: string;
  message: string;
}

export const batchWindowTime = 10000; // 10 seconds

const batchEventTypes = new Set(["downloadPhoto", "scrollWheelDown", "scrollWheelUp"]);
const emptyItems: ITelemetryItem[] = [];

function getItemsFromEvent(event: ITelemetryEvent, sessionContext: ISessionContext): ITelemetryItem[] {
  const eventType = event.action.toLowerCase();
  const page = sessionContext.getPage();

  const commonProperties = {
    AccountType:
      sessionContext.accountType === "business"
        ? "Business"
        : sessionContext.authenticated()
        ? sessionContext.migrated
          ? "ConsumerOnSPO"
          : "Consumer"
        : "ConsumerAnonymous"
  };

  switch (eventType) {
    case "engagement": {
      const { action, scenarioName, name, ...extraData } = event;
      const data = {
        ...commonProperties,
        CurrentPage: page.current,
        Name: `PhotosEngagement.${scenarioName || name}`,
        ...prepareExtraData(action, extraData, sessionContext)
      };

      return [{ name: engagementTable, data }];
    }

    case "exception": {
      const { action, colno, error, filename, lineno, name, scenarioName, stack, ...extraData } = event;

      if (name === "unhandledException") {
        const data = {
          ...commonProperties,
          Name: `OneDrivePhotos.unhandledException`,
          UnhandledError_col: colno,
          UnhandledError_filename: filename,
          UnhandledError_line: lineno,
          UnhandledError_message: error,
          UnhandledError_stack: stack,
          ...prepareExtraData(action, extraData, sessionContext)
        };

        return [{ name: unhandledErrorTable, data }];
      } else {
        const ResultCode = error?.code || error || "noErrorCode";
        const Error = error?.message || error;
        const data = {
          ...commonProperties,
          Error,
          Name: `OneDrivePhotos.${scenarioName || name}`,
          ResultCode,
          ResultType: getFailureType(ResultCode, Error),
          WebLog_EventType: "End",
          ...prepareExtraData(action, extraData, sessionContext)
        };

        return [{ name: qosTable, data }];
      }
    }

    case "fetchcomplete": {
      const { action, duration, ms_cv: CorrelationVector, name, result, scenarioName, url: Api_url, ...extraData } = event;

      // Convert the result into the proper set of reported properties.
      const resultStatus = {
        ResultType: "Success"
      };

      if (result?.status === "rejected") {
        const ResultCode = result.reason?.error?.code || result?.reason?.message || "noResultCode";
        const Error = result.reason?.error?.message || result?.reason?.message;
        const responseStatus = result.reason?.statusCode || result.reason?.error?.statusCode;

        Object.assign(resultStatus, {
          Error,
          ResultCode,
          ResultType: getFailureType(ResultCode, Error),
          responseStatus
        });
      }

      const data = {
        ...commonProperties,
        Api_url,
        CorrelationVector,
        Duration: duration?.toString(),
        Name: `OneDrivePhotos.${scenarioName || name}`,
        WebLog_EventType: "End",
        ...resultStatus,
        ...prepareExtraData(action, extraData, sessionContext)
      };

      return [{ name: qosTable, data }];
    }

    case "fetchstart": {
      const { action, options, name, scenarioName, url: Api_url, ...extraData } = event;

      const data = {
        ...commonProperties,
        Api_url,
        Name: `OneDrivePhotos.${scenarioName || name}`,
        WebLog_EventType: "Start",
        ...prepareExtraData(action, extraData, sessionContext)
      };

      return [{ name: qosTable, data }];
    }

    case "operationcomplete":
    case "scenariocomplete": {
      const { action, children, duration, properties, scenarioName, name } = event;

      let failedSubScenarios: IFailedScenarioProperties[] = populateFailedSubSenarios(event, []);

      let data: any = {
        ...commonProperties,
        CurrentPage: page.current,
        Name: `PhotosScenario.${scenarioName || name}`,
        PreviousPage: page.previous
      };

      const resultType = failedSubScenarios.length > 0 ? "Failure" : "Success";
      const error = failedSubScenarios.length > 0 ? JSON.stringify(failedSubScenarios[0]) : undefined;

      const mergedProperties: any = { ...properties };

      // walk the scenario tree, pulling up properties into the root properties
      // we already copied root, so start with children
      children?.forEach((child) => combineProperties(child, mergedProperties));

      // promote the topmost scenario as the real scenario
      if (scenarioName === "photosAppLoad" && children?.length && children[0].scenarioName) {
        // We take the sub-scenario's name but keep the root duration and error info
        data.Name = `PhotosScenario.${children[0].scenarioName}`;

        // We want to know that we promoted this scenario, as this means we performed a full page load instead of a route switch
        mergedProperties.pageLoad = true;
      }

      Object.assign(
        data,
        prepareExtraData(
          action,
          {
            ...mergedProperties,
            duration,
            ResultType: resultType,
            error
          },
          sessionContext
        )
      );

      let items: ITelemetryItem[] = [{ name: engagementTable, data }];

      // If the scenario/operation is tracked for ASHA, send a customer promise telemetry
      if (properties?.pillar) {
        const pillarData = {
          ...commonProperties,
          CustomerPromise_extraData_excludeTDC: properties.excludeTDC,
          CustomerPromise_extraData_Pillar: properties.pillar,
          CustomerRing: sessionContext.ring,
          Duration: duration?.toString(),
          PerfGoalInMs: properties.perfGoalInMs,
          ResultType: "Success",
          Scenario: scenarioName,
          WebLog_EventType: "End"
        };

        if (failedSubScenarios.length > 0) {
          // TODO: We only want to send one failed customer promise, temporarily promote the first failed subScenario
          const { name: firstFailedSenarioName, ...error } = failedSubScenarios[0];

          Object.assign(pillarData, {
            ErrorCode: error.code,
            CustomerPromise_extraData_veto: firstFailedSenarioName + "Failed", //TODO: Need to have the correct veto name for each error
            CustomerPromise_extraData_errormessage: error.message,
            ResultType: getFailureType(error.code, error.message)
          });
        } else if (duration && properties.perfGoalInMs && duration > properties.perfGoalInMs) {
          // TODO: Temporarily only handles the perfGoal for the parent scenario. Need more work.
          Object.assign(pillarData, {
            ErrorCode: "Missing perf goal",
            ResultType: "Failure",
            CustomerPromise_extraData_veto: "MissingPerfGoal" //TODO: Need to have the correct veto name for each error
          });
        }

        items.push({ name: customerPromiseTable, data: pillarData });
      }

      return items;

      function populateFailedSubSenarios(event: ITelemetryEvent, failed: IFailedScenarioProperties[]): IFailedScenarioProperties[] {
        const { children, result, scenarioName } = event;

        if (result?.status === "rejected" && !result.reason?.isCanceled) {
          failed.push({
            name: scenarioName,
            code: result.reason?.error?.code || result.reason?.message || "noErrorCode",
            message: result.reason?.error?.message || result.reason?.message || JSON.stringify(result.reason)
          });
        }

        for (let child of children || []) {
          populateFailedSubSenarios(child, failed);
        }

        return failed;
      }
    }

    case "sessioncomplete": {
      const { action, name, ...extraData } = event;
      const data = {
        ...commonProperties,
        Name: `Session.${name}`,
        ...prepareExtraData(action, extraData, sessionContext)
      };

      return [{ name: engagementTable, data }];
    }

    case "useraction": {
      const { action, scenarioName, name, ...extraData } = event;
      const data = {
        ...commonProperties,
        Name: `PhotosAction.${scenarioName || name}`,
        CurrentPage: page.current,
        PreviousPage: page.previous,
        ...prepareExtraData(action, extraData, sessionContext)
      };

      return [{ name: engagementTable, data }];
    }

    case "experimentevaluation": {
      const { name, variant, defaultValue = "Default" } = event;

      const data = {
        ...commonProperties,
        CurrentPage: page.current,
        Experiment: name,
        IsInRamp: variant !== defaultValue,
        Variant: variant
      };

      return [{ name: experimentTable, data }];
    }

    default:
      return emptyItems;
  }
}

function combineProperties(event: ITelemetryEvent, target: { [property: string]: any }): void {
  // Only combine component renders
  if (event.scenarioType === "componentRender" && event.properties) {
    for (const key of Object.keys(event.properties)) {
      // Having duplicate telemetry properties is most likely a mistake
      // only the first encountered property will be sent for telemetry
      // Please consider picking a different property name
      if (target[key] != null) {
        console.warn(
          `Duplicate telemetry property ${key} found, existing value ${target[key]} new value ${event.properties[key]}, skipping it. Event is `,
          event
        );
        continue;
      }

      target[key] = event.properties[key];
    }
  }

  event.children?.forEach((child) => combineProperties(child, target));
}

function prepareExtraData(action: string, data: { [property: string]: any }, sessionContext: ISessionContext): any {
  const extraData: { [property: string]: any } = {
    Standalone: sessionContext.standalone || false,
    ...data
  };
  const payloadExtraData: { [key: string]: any } = {};

  for (let key in extraData) {
    const value = extraData[key];
    payloadExtraData[`${telemetryTypePrefixMapping[action]}_extraData_${key}`] = typeof value === "object" ? JSON.stringify(value) : value;
  }

  return payloadExtraData;
}

export function useOneDS(featureContext: IFeatureContext): ((event: ITelemetryEvent) => void) | void {
  const eventContext = React.useContext(EventContext);
  const sessionContext = React.useContext(SessionContext);

  const [oneDsLogger] = React.useState(() => new AppInsightsCore());
  const [instanceId] = React.useState(v4());

  const batchedEvents = React.useRef<{ [key: string]: ITelemetryEvent }>({});
  const pgIsEnabled = useSubscription(featureContext.featureEnabled("EnablePrivacyGuard"));

  const { setInterval } = useInterval();

  const sendItems = React.useCallback(
    (logItems: IExtendedTelemetryItem[] | ITelemetryItem[]) => {
      // istanbul ignore next - DEVELOPMENT: Show OneDS telemetry logs in the console.
      if (process.env.NODE_ENV !== "test" && (sessionContext.environment === "development" || getCookie("Development"))) {
        if (logItems.length) {
          for (let index = 0; index < logItems.length; index++) {
            console.debug(logItems[index].name, logItems[index]);
          }
        }
      }

      for (let logItem of logItems) {
        oneDsLogger.track(logItem);
      }
    },
    [oneDsLogger, sessionContext]
  );

  const sendBatchedItems = React.useCallback(() => {
    const items = Object.keys(batchedEvents.current).reduce((accumulator: IExtendedTelemetryItem[] | ITelemetryItem[], action: string) => {
      const item = batchedEvents.current[action];
      const { totalDuration, ...eventData } = item;

      // Set the average duration for this batched item
      eventData.duration = Math.ceil(totalDuration / item.count);

      // Add the items generated from this event to the resulting items batch
      accumulator.push(...getItemsFromEvent(eventData, sessionContext));
      return accumulator;
    }, []);

    // clean all entries
    batchedEvents.current = {};

    sendItems(items);
  }, [sendItems, sessionContext]);

  setInterval(sendBatchedItems, batchWindowTime);

  // Ensure an ariaToken has been defined, if not telemetry events
  if (sessionContext.ariaToken) {
    try {
      // Based on the users current authentication and migration state record the details.
      const authenticated = sessionContext.authenticated();
      const clientName = sessionContext.accountType === "business" ? "ODBPhotos Web" : "ODCPhotos Web";
      const environment =
        sessionContext.accountType === "business"
          ? "ODBPhotos"
          : `ODCPhotos_${sessionContext.ring === "dogfood" ? "DF" : sessionContext.environment === "production" ? "Prod" : "Dev"}`;
      const workload = sessionContext.accountType === "business" ? "ODBPhotos" : "ODCPhotos";

      // Setup the properties plugin with all the static fields for each event.
      const propertiesPlugin = new PropertiesPlugin();
      propertiesPlugin.setProperty("AppInfo.Manifest", "photos.manifest"); // The manifest comes from coreConfig.ts on the server.
      propertiesPlugin.setProperty("AppInfo.Version", sessionContext.clientVersion);
      propertiesPlugin.setProperty("BrowserIsMobile", !!navigator.userAgent.toLowerCase().match(/mobile/i));
      propertiesPlugin.setProperty("BrowserUserAgent", navigator.userAgent);
      propertiesPlugin.setProperty("ClientName", clientName);
      propertiesPlugin.setProperty("Environment", environment);
      propertiesPlugin.setProperty("IsAuthenticated", authenticated && sessionContext.userProfile ? "1" : "0");
      propertiesPlugin.setProperty("Workload", workload);
      propertiesPlugin.setProperty("InstanceId", instanceId);

      if (sessionContext.session) {
        propertiesPlugin.setProperty("SessionId", sessionContext.session);
      }

      const propertiesPluginConfig: IPropertyConfiguration = {
        populateBrowserInfo: true
      };

      var privacyGuard: PrivacyGuardPlugin = new PrivacyGuardPlugin();
      const privacyGuardPluginConfig = {
        notificationEventName: privacyGuardEvent,
        iKey: sessionContext.ariaToken,
        enabled: pgIsEnabled,
        disableAdvancedScans: true,
        scanForUrls: false
      };

      const coreConfig: IExtendedConfiguration = {
        channels: [[new PostChannel()]],
        cookieCfg: { enabled: false },
        endpointUrl: sessionContext.telemetryEndpoint,
        instrumentationKey: sessionContext.ariaToken,
        extensions: [propertiesPlugin],
        extensionConfig: {
          [propertiesPlugin.identifier]: propertiesPluginConfig,
          [privacyGuard.identifier]: privacyGuardPluginConfig
        }
      };

      // Initialize the oneDS SDK
      oneDsLogger.initialize(coreConfig, [privacyGuard]);

      if (authenticated && sessionContext.userId) {
        // istanbul ignore else - Con't figure out how to remove BigInt from window.
        const userId = "BigInt" in window ? BigInt.asIntN(64, BigInt(`0x${sessionContext.userId}`)).toString(10) : sessionContext.userId;
        propertiesPlugin.setProperty("UserInfo_Id", userId);

        // Add this to the propertiesPlugin context if available.
        const pluginContext = propertiesPlugin.getPropertiesContext();
        if (pluginContext && pluginContext.user) {
          propertiesPlugin.getPropertiesContext().user.localId = `m:${userId}`;
        }
      }

      // Return the event reporting function to the caller
      return (event: ITelemetryEvent): void => {
        if (batchEventTypes.has(event.name)) {
          // Add the current event to the batch
          const { action, name, result, duration, error } = event;
          const itemKey = `${action}-${name}-${result?.status || ""}-${error?.code || error?.message || ""}`;
          if (batchedEvents.current[itemKey]) {
            batchedEvents.current[itemKey].count++;
            batchedEvents.current[itemKey].totalDuration += duration;
          } else {
            batchedEvents.current[itemKey] = {
              ...event,
              count: 1,
              totalDuration: duration
            };
          }
        } else {
          sendItems(getItemsFromEvent(event, sessionContext));
        }
      };
    } catch (error) {
      eventContext.dispatchEvent("telemetryAvailable", { action: "exception", name: "1dsInitialization", error });
    }
  }
}
